import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Box, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import QrCodeIcon from '@mui/icons-material/QrCode';
import AutorenewIcon from '@mui/icons-material/Autorenew';

import { getSuppliersState } from '../../../features/store/get_suppliers';
import { getSupplierStatusIcon, getUpdateSupplierStatus } from '../../common/utils/supplier_utils';
import { CustomIconButton } from '../../common/button';
import { getNewSupplierKeyNotificationData, getSupplierKeyNotificationData, updateSupplierStatusNotificationData } from '../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../core/enums/common/api';
import useNotification from '../../../core/hooks/use_notification';
import { updateSupplierStatusAction, updateSupplierStatusState } from '../../../features/store/update_supplier_status';
import { getSupplierKeyAction, getSupplierKeyState } from '../../../features/store/get_supplier_key';
import { getNewSupplierKeyAction, getNewSupplierKeyState } from '../../../features/store/get_new_supplier_key';
import { GET_ROUTE_SUPPLIERS_EDIT } from '../../../core/constants';
import ConfirmDialog from '../common/confirm_dialog';

const SuppliersList = ({ refreshList }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { showNotification, showErrorMessage } = useNotification();

    const getSuppliers = useSelector(getSuppliersState);
    const updateSupplierStatus = useSelector(updateSupplierStatusState);
    const getSupplierKey = useSelector(getSupplierKeyState);
    const getNewSupplierKey = useSelector(getNewSupplierKeyState);

    const [activeActionId, setActiveActionId] = useState(null);

    const onUpdateSupplierStatus = (supplier) => {
        setActiveActionId(supplier?.id);
        const command = getUpdateSupplierStatus(supplier?.active);

        dispatch(updateSupplierStatusAction({ id: supplier?.id, form: { command } }))
            .then(response => {
                showNotification(updateSupplierStatusNotificationData(response));
                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && refreshList();
                setActiveActionId(null);
            });
    };

    const onGetSupplierKey = (supplier) => {
        setActiveActionId(supplier?.id);
        dispatch(getSupplierKeyAction(supplier?.id))
            .then(response => {
                showErrorMessage(getSupplierKeyNotificationData(response));
                setActiveActionId(null);
            });
    };

    const onGetNewSupplierKey = (supplier) => {
        setActiveActionId(supplier?.id);
        dispatch(getNewSupplierKeyAction(supplier?.id))
            .then(response => {
                showErrorMessage(getNewSupplierKeyNotificationData(response));
                setActiveActionId(null);
            });
    };

    return (
        <TableContainer component={ Paper } variant="outlined">
            <Table stickyHeader>
                <TableHead>
                    <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                        <TableCell>Numer</TableCell>
                        <TableCell>Imię</TableCell>
                        <TableCell>Nazwisko</TableCell>
                        <TableCell align="center">Nr telefonu</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell align="center">Aktywny</TableCell>
                        <TableCell align="center" sx={ { width: 180 } }>

                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {
                        getSuppliers?.data?.content?.map(supplier => (
                            <TableRow key={ supplier?.id } hover>
                                <TableCell sx={ { fontWeight: 600 } }>{ supplier?.id }</TableCell>
                                <TableCell>{ supplier?.name }</TableCell>
                                <TableCell>{ supplier?.surname }</TableCell>
                                <TableCell align="center">{ supplier?.phoneNumber }</TableCell>
                                <TableCell>{ supplier?.email }</TableCell>
                                <TableCell align="center">
                                    { getSupplierStatusIcon(supplier?.active) }
                                </TableCell>
                                <TableCell align="center">
                                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={ 1 }>

                                        <Tooltip title="Edytuj zaopatrzeniowca">
                                            <IconButton
                                                color="primary"
                                                size="small"
                                                onClick={ () => navigate(GET_ROUTE_SUPPLIERS_EDIT(supplier?.id)) }
                                            >
                                                <EditIcon fontSize="small"/>
                                            </IconButton>
                                        </Tooltip>

                                        <CustomIconButton
                                            title={ supplier?.active ? "Dezaktywuj" : "Aktywuj" }
                                            color={ supplier?.active ? "success" : "error" }
                                            size="small"
                                            isLoading={ updateSupplierStatus?.loading && activeActionId === supplier?.id }
                                            onClick={ () => onUpdateSupplierStatus(supplier) }
                                            icon={ supplier?.active ? <ToggleOnIcon fontSize="small"/> :
                                                <ToggleOffIcon fontSize="small"/> }
                                        />

                                        <CustomIconButton
                                            title="Pobierz kod QR"
                                            color="primary"
                                            size="small"
                                            isLoading={ getSupplierKey?.loading && activeActionId === supplier?.id }
                                            onClick={ () => onGetSupplierKey(supplier) }
                                            icon={ <QrCodeIcon fontSize="small"/> }
                                        />

                                        <Tooltip title="Wygeneruj nowy kod QR">
                                            <Box component="span">
                                                <ConfirmDialog
                                                    isLoading={ getNewSupplierKey?.loading && activeActionId === supplier?.id }
                                                    maxWidth="sm"
                                                    title="Czy na pewno chcesz wygenerować nowy kod zaopatrzeniowca? Stary kod przestanie działać i nie będzie można go przywrócić."
                                                    buttonText={ <AutorenewIcon fontSize="small"/> }
                                                    secondaryAction={ () => {
                                                    } }
                                                    secondaryText="Anuluj"
                                                    primaryAction={ () => onGetNewSupplierKey(supplier) }
                                                    primaryText="Wygeneruj kod"
                                                />
                                            </Box>
                                        </Tooltip>

                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))
                    }

                    { (!getSuppliers?.data?.content || getSuppliers?.data?.content.length === 0) && (
                        <TableRow>
                            <TableCell colSpan={ 7 } align="center" sx={ { py: 4, color: 'text.secondary' } }>
                                Brak wyników do wyświetlenia.
                            </TableCell>
                        </TableRow>
                    ) }
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default SuppliersList;